import { createSkillTestApi, updateSkillTestApi } from "@/api/services/global/master";
import { TGetSkillTestListOutputDto } from "@/api/services/global/master/dtos/get-master-skill-test-list.dto";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { masterDataSkillTestSchema } from "@/features/master-data/schema/masterDataSkillTestSchema";
import { TMasterSkillTestForm } from "@/features/master-data/types/masterDataSkillTestForm";
import { setErrorForm, showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { useForm } from "react-hook-form";

type TSkillTestFormModalProps = {
  onClose: () => void;
  isOpen: boolean;
  selectedSkillTest?: TGetSkillTestListOutputDto;
};

export const SkillTestFormModal: FC<TSkillTestFormModalProps> = ({ onClose, isOpen, selectedSkillTest }) => {
  const queryClient = useQueryClient();

  const defaultValues = useMemo(
    () => ({
      skillTestName: selectedSkillTest ? selectedSkillTest.skillTestName : undefined,
      cfSkillTestRenewVisaCsvCode: selectedSkillTest ? selectedSkillTest.cfSkillTestRenewVisaCsvCode : undefined,
    }),
    [selectedSkillTest],
  );

  const methods = useForm<TMasterSkillTestForm>({
    resolver: yupResolver(masterDataSkillTestSchema),
    mode: "onBlur",
    defaultValues,
  });

  const { control } = methods;

  const onSubmit = async (data: TMasterSkillTestForm) => {
    try {
      if (selectedSkillTest) {
        const rs = await updateSkillTestApi({
          skillTests: [
            {
              id: toNumber(selectedSkillTest.id),
              skillTestName: data.skillTestName,
              cfSkillTestRenewVisaCsvCode: data.cfSkillTestRenewVisaCsvCode,
            },
          ],
        });
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      } else {
        const rs = await createSkillTestApi({
          skillTestName: data.skillTestName,
          cfSkillTestRenewVisaCsvCode: data.cfSkillTestRenewVisaCsvCode,
        });
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      }
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_SKILL_TEST] });
      onClose();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal title={selectedSkillTest ? "編集" : "追加"} isOpen={isOpen} enableFooter={false} onClose={onClose} enableCloseIcon={false}>
      <Form methods={methods} onSubmit={onSubmit} schema={masterDataSkillTestSchema}>
        <Stack gap={3} my={3}>
          <Grid item xs={6}>
            <FormField control={control} name="skillTestName">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="cfSkillTestRenewVisaCsvCode">
              <Input />
            </FormField>
          </Grid>
        </Stack>
        <Stack flexDirection="row" justifyContent="center" gap="24px">
          <Button variant="outline" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="submit">保存</Button>
        </Stack>
      </Form>
    </Modal>
  );
};
